import React, {lazy, memo, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {FloatButtonContext} from '@assets/contexts/floatButtonContext';
import useTheme from './useTheme';
import {checkShowNewsletterPopup} from '@scripttag/helpers/storage';
import {i18nCampaign, i18nWidget} from '@functions/helpers/translation/i18n';
import {useWindowSize} from '@scripttag/hooks/useWindowSize';
import {LIGHT_MODE} from '@assets/const/liveChat';
import WidgetLabelChangedProvider from '@assets/pages/Widget/WidgetLabelChangedProvider';
import {EXPAND_PAGES} from '@assets/const/widgetSetting';
import {flatten} from '@avada/utils';
import {
  getWidgetSettingsStorage,
  setWidgetSettingsStorage
} from '@scripttag/helpers/sessionHelpers';
import {getDefaultState, handleTriggerState} from '@assets/helpers/widget/tabActionHelperWidget';
import useSwipeDownCloseModal from '@assets/hooks/modal/useSwipeDownCloseModal';
import isRemovedBranding from '@scripttag/helpers/branding/isRemovedBranding';
import {hasEmailTag} from '@functions/helpers/utils/presentText';

const PreviewWrapper =
  process.env.BUILD_TARGET !== 'scripttag' &&
  lazy(() => import('@assets/components/PreviewWrapper'));
const Popup = lazy(() => import('./Popup'));
const WelcomePopupContainer = lazy(() =>
  import('@assets/components/Preview/WidgetWelcomePopup/WelcomePopupContainer')
);
const MUIThemeCustomWrapper = lazy(() =>
  import('@assets/components/Preview/MUIThemeCustomWrapper')
);

function WidgetFAQ(props) {
  return (
    <WidgetLabelChangedProvider isLabelChanged={!!props.isLabelChanged}>
      <WidgetFAQComponent {...props} />
    </WidgetLabelChangedProvider>
  );
}

export default memo(WidgetFAQ);

const WidgetWrapper = ({isPreview, isAISettings, appCss, isMobile, children}) => {
  if (!isPreview) return children(isMobile);
  return (
    <PreviewWrapper isAISettings={isAISettings} appCss={appCss}>
      {children}
    </PreviewWrapper>
  );
};

const WidgetFAQComponent = ({
  settings,
  categories,
  faqsTranslation = [],
  translatedQuestions = [],
  translatedFeaturedQuestions = [],
  featuredQuestions,
  translations = {},
  translation,
  defaultPage = 'homePage',
  defaultTab = 0,
  defaultHistory = ['homePage'],
  previewPage = '',
  wizard = false,
  isPreview,
  getData,
  postData,
  putData,
  openModalWidget = null,
  setOpenModalWidget = _ => {},
  handleChangeTranslation = (_1, _2) => {},
  disableBack = false,
  open,
  setOpen,
  selectedTab,
  setSelectedTab,
  isShowDesktop,
  setIsShowDesktop,
  openWelcomePopup,
  setOpenWelcomePopup,
  openNewsletterPopup,
  setOpenNewsletterPopup,
  chatInstance,
  setChatInstance,
  setCustomerReading = () => {},
  refreshTimeoutNotificationMessage = () => {},
  isAvailableTime,
  setIsStaffOnline,
  handleTrackInSession = () => {},
  children,
  isAISettings,
  previewChildRef,
  activeCampaigns,
  setActiveCampaigns,
  ...widgetProps
}) => {
  const {
    appCss,
    floatBtnPosition,
    isGradient,
    showNavigation: isShowNavigation,
    colorMode = LIGHT_MODE,
    isLiveChat
  } = settings;

  const {theme, classes} = useTheme(settings);
  const [isNewsletterPopup, isQuickReplies, isPreviewAI] = [
    'newsletter-popup',
    'quick-replies',
    'ai-assistant'
  ].map(x => x === previewPage);

  const [currentTab, setCurrentTabOrg] = useState(() => {
    return getDefaultState(defaultTab, 'currentTab', isPreview);
  });
  const setCurrentTab = val => {
    handleTriggerState({cb: val, prev: currentTab, key: 'currentTab', setState: setCurrentTabOrg});
  };
  const [isShowEmailTranscript, setIsShowEmailTranscript] = useState(false);
  const [navigationLength, setNavigationLength] = useState(0);
  const [valueSearch, setValueSearch] = useState('');
  const [isExpand, setIsExpandOrg] = useState(false);
  const setIsExpand = val => {
    handleTriggerState({cb: val, prev: isExpand, key: 'isExpand', setState: setIsExpandOrg});
  };
  const [dismissNavigation, setDismissNavigation] = useState(!isShowNavigation);
  const [currentPage, setCurrentPage] = useState(() => {
    return getDefaultState(defaultPage, 'currentPage', isPreview);
  });
  const [history, setHistoryOrg] = useState(defaultHistory);
  const setHistory = val => {
    handleTriggerState({cb: val, prev: history, key: 'history', setState: setHistoryOrg});
  };
  const [questionDetail, setQuestionDetail] = useState(null);
  const [categoryDetail, setCategoryDetail] = useState(null);
  const [categoriesList, setCategoriesList] = useState(isPreview ? categories : faqsTranslation);
  const [showInput, setShowInput] = useState(true);
  const [order, setOrder] = useState(null);
  const previewQuestions = useMemo(
    () => flatten(categories.map(c => [...c.questions?.filter(q => q.published)])),
    [categories]
  );
  const position = useMemo(() => floatBtnPosition.replace('_', ' '), [floatBtnPosition]);
  const {isMobile} = useWindowSize();

  const floatButtonSizeClass = `button-${settings.floatBtnSize.slice(0, 2)}`;
  const adminClass = [position, isPreview && 'admin'].filter(Boolean).join(' ');

  const popupClass = [
    'Avada-Faqs_Popup',
    adminClass,
    currentPage,
    !isPreviewAI && !isRemovedBranding({settings}) && 'hasFooter',
    isShowNavigation && !dismissNavigation && 'hasNavigation',
    `h-${settings.coverageRatio}`,
    ((isExpand && isShowDesktop && isPreview) || (!isMobile && isExpand && !isPreview)) &&
      'Avada-Faqs_Popup--Expand',
    floatButtonSizeClass,
    (isPreview ? !isShowDesktop : isMobile) ? 'mobile' : 'desktop',
    open && 'Avada-Faqs_Popup--Open',
    colorMode !== LIGHT_MODE && 'darkMode',
    isGradient && 'isGradient'
  ].filter(Boolean);

  const isShowSubscribeForm = useMemo(
    () => (isPreview ? isNewsletterPopup : checkShowNewsletterPopup(true)),
    []
  );

  // restore navigation to showing when back from chat page
  useEffect(() => {
    if (currentPage !== 'chatDetail' && dismissNavigation) setDismissNavigation(false);
  }, [currentPage, dismissNavigation]);

  useEffect(() => {
    // toggle popup from other states, like theme settings
    if (isPreview && openModalWidget !== null) setOpen(openModalWidget);
  }, [isPreview, openModalWidget]);

  const onChangePage = page => {
    setWidgetSettingsStorage({currentPage: page});
    const valueToggle = EXPAND_PAGES.includes(page);
    if (valueToggle === isExpand) {
      setCurrentPage(page);
      return;
    }
    setIsExpand(valueToggle);
    setCurrentPage(page);
    // setTimeout(() => setCurrentPage(page), isShowNavigation ? 200 : 0);
  };

  const options = useMemo(
    () =>
      [
        'homePage',
        (isLiveChat || settings.isEnableNewsletterPopup || settings.isEnableQuickReplies) &&
          'chatDetail',
        settings.isOrderTracking && 'trackTab',
        (settings.isFeaturedArticles || settings.isCategory) && 'categoriesList'
      ].filter(Boolean),
    [
      isLiveChat,
      settings.isOrderTracking,
      settings.isFeaturedArticles,
      settings.isCategory,
      settings.isEnableNewsletterPopup
    ]
  );

  const handleBackTab = page => {
    switch (page) {
      case 'questionDetail':
      case 'categoryDetail':
        return handleBackTab('categoriesList');
      case 'orderDetail':
        return handleBackTab('trackTab');
      default:
        setCurrentTab(options.indexOf(page));
        break;
    }
  };

  const prevPage = page => {
    if (page) {
      // back to specified page
      onChangePage(page);
      // remove trace from the specified page
      setHistory(prev => prev.splice(0, prev.indexOf(page) + 1));
      return;
    }

    // back to the nearest page
    onChangePage(history[history.length - 2]);
    // back tab at navigation
    isShowNavigation && handleBackTab(history[history.length - 2]);
    // remove the nearest page from history
    setHistory(prev => prev.splice(0, prev.length - 1));
  };

  const nextPage = page => {
    onChangePage(page);
    setHistory(prev => [...prev, page]);
  };

  const backToHomePage = () => {
    onChangePage('homePage');
    setHistory(['homePage']);
  };

  const {swiper} = useSwipeDownCloseModal({
    currentPage,
    onSwipeDown: () => setOpen(() => false)
  });

  function i18n(phrase, variables, {useEmailTag = true, prefix} = {}) {
    const result = i18nWidget({phrase, variables, translation, useEmailTag, prefix});
    if (!useEmailTag) return result;
    return hasEmailTag(result) ? <span dangerouslySetInnerHTML={{__html: result}} /> : result;
  }

  const defaultCampaignVar = (() => {
    const customer = window.AVADA_APP_FAQS_CUSTOMER || {};
    return {customer_name: customer.name || ''};
  })();

  const props = {
    ...widgetProps,
    classes,
    setOpen,
    nextPage,
    backToHomePage,
    order,
    isShowEmailTranscript,
    setIsShowEmailTranscript,
    setOrder,
    prevPage,
    history,
    featuredQuestions,
    settings,
    categories,
    faqsTranslation,
    translatedQuestions,
    translatedFeaturedQuestions,
    questionDetail,
    setQuestionDetail: val => {
      setWidgetSettingsStorage({questionDetail: val});
      setQuestionDetail(val);
    },
    categoryDetail,
    setCategoryDetail: val => {
      setWidgetSettingsStorage({categoryDetail: val});
      setCategoryDetail(val);
    },
    categoriesList,
    setCategoriesList: val => {
      setWidgetSettingsStorage({categoriesList: val});
      setCategoriesList(val);
    },
    wizard,
    isPreview,
    setOpenModalWidget,
    selectedTab,
    setSelectedTab,
    isShowDesktop,
    setIsShowDesktop,
    currentPage,
    setCurrentPage: onChangePage,
    currentTab,
    setCurrentTab,
    setHistory,
    isExpand,
    setIsExpand,
    navigationLength,
    setNavigationLength,
    previewPage,
    dismissNavigation,
    setDismissNavigation,
    chatInstance,
    setChatInstance,
    handleChangePrimaryLanguage: val => handleChangeTranslation('primaryLanguage', val),
    translations,
    getData,
    postData,
    putData,
    disableBack,
    previewQuestions,
    setValueSearch,
    valueSearch,
    isDarkMode: colorMode !== LIGHT_MODE,
    isShowSubscribeForm,
    isAvailableTime,
    setIsStaffOnline,
    swiper,
    isQuickReplies,
    showInput,
    setShowInput,
    setCustomerReading,
    refreshTimeoutNotificationMessage,
    handleTrackInSession,
    previewChildRef,
    isAISettings,
    i18n,
    i18nCampaign: (phrase, campaignId) =>
      i18nCampaign({phrase, campaignId, translation, variables: defaultCampaignVar}),
    activeCampaigns,
    setActiveCampaigns
  };

  // reset to default state when editing chatbox settings
  useEffect(() => {
    if (!isPreview) return;
    setHistory(defaultHistory);
    onChangePage(defaultPage);
    setCurrentTab(defaultTab);
  }, [options, isShowNavigation]);

  // avoid body zoom in widget when focus input
  useEffect(() => {
    const elem = document.querySelector('meta[name="viewport"]');
    if (!elem) return;
    elem.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
    );
  }, []);

  useEffect(() => {
    return () => {
      const stateElm = document.querySelector('#openViaInlineButton');
      if (stateElm) document.body.removeChild(stateElm);
    };
  }, []);

  useEffect(() => {
    if (!window.ChattyJS) return;
    window.ChattyJS.registerHandler('openPage', nextPage);
    return () => window.ChattyJS.unregisterHandler('openPage');
  }, []);

  useEffect(() => {
    const floatButtonElm = document.getElementById('Avada-FAQ_WidgetTrigger');
    if (!floatButtonElm) return;

    const handleHashUrlChange = () => {
      const tabInfo = getWidgetSettingsStorage();
      const {currentTab, currentPage, history} = tabInfo;
      (currentTab || currentTab === 0) && setCurrentTab(currentTab);
      currentPage && setCurrentPage(currentPage);
      history && Array.isArray(history) && setHistory(history);
    };
    floatButtonElm.addEventListener('customEvent', handleHashUrlChange);
    return () => floatButtonElm.removeEventListener('customEvent', handleHashUrlChange);
  }, []);

  useEffect(() => {
    const tabInfo = getWidgetSettingsStorage();
    const {history, isExpand, categoriesList, categoryDetail, questionDetail} = tabInfo;
    history && setHistoryOrg(history);
    isExpand && setIsExpandOrg(isExpand);
    categoriesList && setCategoriesList(categoriesList);
    categoryDetail && setCategoryDetail(categoryDetail);
    questionDetail && setQuestionDetail(questionDetail);
  }, []);

  useEffect(() => {
    if (isQuickReplies) nextPage('chatDetail');
  }, [isQuickReplies]);

  const bottomPopup = (() => {
    switch (settings.floatBtnSize) {
      case '40px':
      default:
        return '65px';
      case '55px':
        return '85px';
      case '70px':
        return '95px';
    }
  })();

  return (
    <FloatButtonContext.Provider value={props}>
      <WidgetWrapper {...{isPreview, appCss, isMobile, isAISettings}}>
        {isMobile => (
          <MUIThemeCustomWrapper theme={theme}>
            {open && (
              <div id="Avada_Faq_Popup-Container">
                <div
                  className={popupClass.join(' ')}
                  style={{
                    ...((isPreview && isShowDesktop) || !isPreview ? {bottom: bottomPopup} : {})
                  }}
                >
                  {!isPreviewAI && <HeaderThumbnail swiper={swiper} />}
                  <Popup {...{currentPage, isMobile}} />
                </div>
              </div>
            )}
            <WelcomePopupContainer
              {...{
                settings,
                adminClass,
                previewPage,
                openWelcomePopup,
                setOpenWelcomePopup,
                openNewsletterPopup,
                setOpenNewsletterPopup,
                bottomPopup,
                isPreview,
                nextPage,
                setCurrentTab,
                putData,
                open,
                setOpen,
                wizard,
                activeCampaigns,
                setActiveCampaigns,
                setShowInput,
                i18n: props.i18n,
                i18nCampaign: props.i18nCampaign,
                isFromCampaignPreview: props.isFromCampaignPreview
              }}
            />
            {children}
          </MUIThemeCustomWrapper>
        )}
      </WidgetWrapper>
    </FloatButtonContext.Provider>
  );
};

const HeaderThumbnail = ({swiper}) => {
  return (
    <div style={{height: '64px', position: 'absolute', width: '100%', top: 0, left: 0}}>
      <div className="Avada-Faqs-Header__Thumbnail--Swiper" ref={swiper}>
        <div className="Avada-Faqs-Header__Thumbnail--SwiperChild"></div>
      </div>
    </div>
  );
};

WidgetWrapper.propTypes = {
  children: PropTypes.func,
  isPreview: PropTypes.bool,
  isAISettings: PropTypes.bool,
  appCss: PropTypes.any,
  isMobile: PropTypes.bool
};

WidgetFAQ.propTypes = WidgetFAQComponent.propTypes = {
  settings: PropTypes.object,
  defaultPage: PropTypes.string,
  defaultTab: PropTypes.number,
  defaultHistory: PropTypes.array,
  categories: PropTypes.array,
  faqsTranslation: PropTypes.array,
  translatedQuestions: PropTypes.array,
  translatedFeaturedQuestions: PropTypes.array,
  questions: PropTypes.array,
  featuredQuestions: PropTypes.array,
  translation: PropTypes.object,
  previewPage: PropTypes.string,
  wizard: PropTypes.bool,
  isPreview: PropTypes.bool,
  classes: PropTypes.any,
  getData: PropTypes.func,
  postData: PropTypes.func,
  putData: PropTypes.func,
  isLabelChanged: PropTypes.any,
  translations: PropTypes.object,
  handleChangeTranslation: PropTypes.func,
  openModalWidget: PropTypes.any,
  setOpenModalWidget: PropTypes.func,
  disableBack: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func,
  isShowDesktop: PropTypes.bool,
  setIsShowDesktop: PropTypes.func,
  openWelcomePopup: PropTypes.bool,
  setOpenWelcomePopup: PropTypes.func,
  chatInstance: PropTypes.any,
  setChatInstance: PropTypes.func,
  isAvailableTime: PropTypes.bool,
  setIsStaffOnline: PropTypes.func,
  handleTrackInSession: PropTypes.func,
  children: PropTypes.any,
  campaigns: PropTypes.array
};
